import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button, Input, Label } from '@zafroi/ui-kit';
import { Loader2, Mail, Trash2, UserPlus } from 'lucide-react';
import {
  addNotificationRecipient,
  listNotificationRecipients,
  removeNotificationRecipient,
} from '@/services/engage-api';
import { ApiError } from '@/services/api-client';

/**
 * Tenant-managed recipients for account notifications (ADR-045) — access
 * suspended / restored emails. When the list is empty, the backend falls back
 * to the tenant's Admin-role users, so notifications always reach someone.
 */
export function NotificationRecipientsPanel() {
  const queryClient = useQueryClient();
  const { data: recipients, isLoading } = useQuery({
    queryKey: ['notification-recipients'],
    queryFn: listNotificationRecipients,
  });

  const [email, setEmail] = useState('');
  const [label, setLabel] = useState('');

  const add = useMutation({
    mutationFn: () =>
      addNotificationRecipient({ email: email.trim(), label: label.trim() || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-recipients'] });
      setEmail('');
      setLabel('');
      toast.success('Recipient added');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Couldn’t add recipient'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => removeNotificationRecipient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-recipients'] });
      toast.success('Recipient removed');
    },
    onError: () => toast.error('Couldn’t remove recipient'),
  });

  const emailValid = /^\S+@\S+\.\S+$/.test(email.trim());
  const canAdd = emailValid && !add.isPending;

  return (
    <section className="rounded-lg border border-border bg-card p-5">
      <header className="mb-4">
        <h2 className="text-sm font-semibold text-foreground">Notification recipients</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Who receives account emails (access suspended / restored). Add the owner, admins, or a
          shared inbox like <span className="font-medium">accounts@…</span>. When this list is empty,
          notifications go to your Admin-role users by default.
        </p>
      </header>

      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[220px]">
          <Label htmlFor="nr-email" className="text-xs">
            Email
          </Label>
          <Input
            id="nr-email"
            type="email"
            placeholder="name@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canAdd) add.mutate();
            }}
          />
        </div>
        <div className="flex-1 min-w-[160px]">
          <Label htmlFor="nr-label" className="text-xs">
            Label (optional)
          </Label>
          <Input
            id="nr-label"
            placeholder="e.g. Business Head"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canAdd) add.mutate();
            }}
          />
        </div>
        <Button onClick={() => add.mutate()} disabled={!canAdd}>
          {add.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <UserPlus className="h-4 w-4" />
          )}
          Add
        </Button>
      </div>

      <div className="mt-4">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : recipients && recipients.length > 0 ? (
          <ul className="divide-y divide-border rounded-md border border-border">
            {recipients.map((r) => (
              <li key={r.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium text-foreground">{r.email}</span>
                {r.label && <span className="text-xs text-muted-foreground">· {r.label}</span>}
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto text-muted-foreground hover:text-destructive"
                  onClick={() => remove.mutate(r.id)}
                  disabled={remove.isPending}
                  aria-label={`Remove ${r.email}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground">
            No recipients yet — account notifications go to your Admin-role users.
          </p>
        )}
      </div>
    </section>
  );
}
